import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import type {
  AnomalyDetail,
  CommandDetail,
  GapDetail,
  ServiceDetail,
  StateChangeDetail,
  TelemetryEvent,
  TelemetryEventKind,
} from "@/lib/telemetry";
import { formatNumber, formatSeconds, formatTs, type TelemetryTz } from "@/components/telemetry/format";
import { cn } from "@/lib/utils";
import { useCatalog, type Translate } from "@/app/localization";

/**
 * One line describing what happened.
 *
 * Descriptive, never diagnostic: "voltage 0 while charging" is a fact in the
 * telemetry; whether that was a tripped protection or a dropped contactor is
 * not something the telemetry knows, and labelling it here would be a guess
 * wearing a fact's clothes.
 *
 * Null when the event carries nothing to describe. Protocol values (states,
 * decoded day/time, anomaly rules and observed values, markers) stay raw.
 */
export function eventDetailText(t: Translate, event: TelemetryEvent): string | null {
  const detail = event.detail;
  if (!detail) return null;

  switch (event.kind) {
    case "state_change": {
      const d = detail as StateChangeDetail;
      const from = d.from ?? "—";
      const to = d.to ?? "—";
      return d.prev_duration_s != null
        ? t("operator.telemetry.detail_state_change_after", { from, to, duration: formatSeconds(d.prev_duration_s) })
        : t("operator.telemetry.detail_state_change", { from, to });
    }
    case "command": {
      const d = detail as CommandDetail;
      const decoded = d.decoded as Record<string, unknown> | null;
      if (decoded && Object.keys(decoded).length) {
        const parts: string[] = [];
        if (decoded.day || decoded.time) parts.push(`${decoded.day ?? ""} ${decoded.time ?? ""}`.trim());
        if (decoded.online_current != null) parts.push(t("operator.telemetry.detail_online_current", { amps: String(decoded.online_current) }));
        if (decoded.offline_current != null) parts.push(t("operator.telemetry.detail_offline_current", { amps: String(decoded.offline_current) }));
        if (decoded.rapid_mode != null) parts.push(t("operator.telemetry.detail_rapid", { state: decoded.rapid_mode ? "on" : "off" }));
        if (parts.length) return parts.join(" · ");
      }
      return d.raw ?? null;
    }
    case "gap": {
      const d = detail as GapDetail;
      return t("operator.telemetry.detail_gap", {
        duration: formatSeconds(d.seconds),
        severity: d.severity === "offline_looking" ? "offline_looking" : "short",
        threshold: formatSeconds(d.offline_threshold_s),
      });
    }
    case "service": {
      const d = detail as ServiceDetail;
      return d.marker === "REJECTED"
        ? t("operator.telemetry.detail_service_rejected")
        : t("operator.telemetry.detail_service_frame", { marker: d.marker || "service" });
    }
    case "anomaly": {
      const d = detail as AnomalyDetail;
      const observed = Object.entries(d.observed ?? {})
        .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join("/") : value}`)
        .join(" ");
      return `${d.rule}${d.sample_count > 1 ? ` ×${d.sample_count}` : ""}${observed ? ` · ${observed}` : ""}`;
    }
    default:
      return null;
  }
}

function anomalySeverity(event: TelemetryEvent): "error" | "warning" {
  const detail = event.detail as AnomalyDetail | null;
  return detail?.severity === "error" ? "error" : "warning";
}

/**
 * What the frame is, expressed as colour rather than as a column.
 *
 * The kind of an event is a fact about the pipeline, not about the charger; a
 * support reader needs to see that a row is an anomaly, not to read the word.
 */
type FrameTone = "error" | "warning" | "gap" | "plain";

function frameTone(row: EventFrameRow): FrameTone {
  const anomaly = row.events.find((e) => e.kind === "anomaly");
  if (anomaly) return anomalySeverity(anomaly);
  if (row.kinds.includes("gap")) return "gap";
  return "plain";
}

/** Soft enough to read a whole table of them, loud enough to find one. */
const TONE_ROW_CLASS: Record<FrameTone, string> = {
  error: "bg-red-500/10 hover:bg-red-500/15",
  warning: "bg-amber-400/10 hover:bg-amber-400/15",
  gap: "bg-amber-400/5 hover:bg-amber-400/10",
  plain: "",
};

const TONE_TEXT_CLASS: Record<FrameTone, string> = {
  error: "text-red-600 dark:text-red-400",
  warning: "text-amber-700 dark:text-amber-400",
  gap: "text-amber-700 dark:text-amber-400",
  plain: "",
};

export function eventRefString(event: TelemetryEvent): string | null {
  const { src_file, src_line } = event.ref ?? {};
  if (!src_file || src_line == null) return null;
  return `${src_file}:${src_line}`;
}

/**
 * One archived frame, with every event derived from it.
 *
 * `message_logger.py:254` writes one CSV row per UDP exchange, carrying BOTH
 * what the charger reported and the command the server answered with. The lake
 * derives several typed events from that one row — a `sample`, a `command`, and
 * whichever of `state_change` / `gap` / `anomaly` applies — and they all inherit
 * that row's `src_file:src_line`, because a ref addresses the source line, not
 * the event. Listing them as separate rows repeats one timestamp, one state, one
 * set of readings and one ref, and reads as duplication of the frame rather than
 * as two sides of it.
 */
export interface EventFrameRow {
  key: string;
  ts: string;
  state: string | null;
  voltage: number | null;
  current: number | null;
  temperature: number | null;
  ref: string | null;
  kinds: TelemetryEventKind[];
  /** What the charger sent: the detail of every non-command event on this frame. */
  sample: string;
  /** What the server sent back on the same frame. */
  response: string;
  /** What a click opens — the most specific event of the frame. */
  primary: TelemetryEvent;
  events: TelemetryEvent[];
}

/** Most specific first — an anomaly beats the bare sample that carried it. */
const FRAME_PRIORITY: Record<TelemetryEventKind, number> = {
  anomaly: 0,
  gap: 1,
  state_change: 2,
  command: 3,
  sample: 4,
  // Never actually reached: service frames have their own table and are filtered
  // out before a frame row is built. Ranked last so that a future path which
  // does let one through cannot outrank a real event.
  service: 5,
};

/**
 * Collapse events onto the frame they came from, preserving the incoming order.
 *
 * Grouped on ref AND timestamp: an event with no ref cannot be shown to share a
 * source line with anything, so it stays its own row rather than being merged on
 * a guess.
 */
export function mergeEventsByFrame(t: Translate, events: TelemetryEvent[]): EventFrameRow[] {
  const rows: EventFrameRow[] = [];
  const byFrame = new Map<string, EventFrameRow>();

  events.forEach((event, index) => {
    const ref = eventRefString(event);
    const key = ref ? `${ref}@${event.ts}` : `orphan-${index}`;
    const existing = ref ? byFrame.get(key) : undefined;
    const row = existing ?? {
      key,
      ts: event.ts,
      state: event.state,
      voltage: event.voltage,
      current: event.current,
      temperature: event.temperature,
      ref,
      kinds: [],
      sample: "",
      response: "",
      primary: event,
      events: [],
    };

    row.events.push(event);
    if (!row.kinds.includes(event.kind)) row.kinds.push(event.kind);
    if (FRAME_PRIORITY[event.kind] < FRAME_PRIORITY[row.primary.kind]) row.primary = event;

    const text = eventDetailText(t, event);
    if (event.kind === "command") {
      if (text) row.response = row.response ? `${row.response} · ${text}` : text;
    } else if (text) {
      row.sample = row.sample ? `${row.sample} · ${text}` : text;
    }
    // A frame's readings are the frame's, but a bare `sample` event is the one
    // that always carries them — never let a null from a sibling win.
    row.state ??= event.state;
    row.voltage ??= event.voltage;
    row.current ??= event.current;
    row.temperature ??= event.temperature;

    if (!existing) {
      rows.push(row);
      if (ref) byFrame.set(key, row);
    }
  });

  for (const row of rows) {
    row.kinds.sort((a, b) => FRAME_PRIORITY[a] - FRAME_PRIORITY[b]);
  }
  return rows;
}

export function EventsTable({
  events,
  tz,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  totalEvents,
}: {
  events: TelemetryEvent[];
  tz: TelemetryTz;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onSelect?: (event: TelemetryEvent) => void;
  totalEvents?: number | null;
}) {
  const { t, catalog } = useCatalog();
  const rows = React.useMemo(() => mergeEventsByFrame(t, events), [events, t, catalog]);

  const columns = React.useMemo<ColumnDef<EventFrameRow, any>[]>(
    () => [
      {
        id: "ts",
        header: t("operator.telemetry.column_time", { zone: tz }),
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">{formatTs(row.original.ts, tz)}</span>
        ),
      },
      {
        id: "state",
        header: t("operator.telemetry.column_state"),
        cell: ({ row }) => <span className="text-xs">{row.original.state ?? "—"}</span>,
      },
      {
        id: "event",
        header: t("operator.telemetry.column_event"),
        cell: ({ row }) => {
          const { sample, response } = row.original;
          const tone = frameTone(row.original);
          // The charger's side leads; the server's answer, when the same frame
          // carries one, follows it as a second line instead of a column.
          const headline = sample || response || t("operator.telemetry.plain_sample");
          const reply = sample && response ? response : null;
          return (
            <span className="block max-w-[38ch] sm:max-w-[62ch]" title={[sample, response].filter(Boolean).join(" · ") || undefined}>
              <span className={cn("block truncate text-xs", TONE_TEXT_CLASS[tone], !sample && !response && "text-muted-foreground")}>
                {headline}
              </span>
              {reply && (
                <span className="block truncate text-[11px] text-muted-foreground">{t("operator.telemetry.server_reply", { reply })}</span>
              )}
            </span>
          );
        },
      },
      {
        id: "readings",
        header: t("operator.telemetry.column_readings"),
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">
            {formatNumber(row.original.voltage)} / {formatNumber(row.original.current)} /{" "}
            {row.original.temperature ?? "—"}
          </span>
        ),
      },
    ],
    [tz, t, catalog],
  );

  return (
    <DataTableShell
      height="auto"
      minHeight="0"
      className="[&>div]:max-h-[480px]"
      footer={
        <>
          <span className="text-xs text-muted-foreground">
            {totalEvents != null
              ? t("operator.telemetry.events_footer_of", { frames: rows.length, events: events.length, total: totalEvents })
              : t("operator.telemetry.events_footer", { frames: rows.length, events: events.length })}
          </span>
          {hasMore && onLoadMore && (
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? t("common.state.loading") : t("common.action.load_more")}
            </Button>
          )}
        </>
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        onRowClick={onSelect ? (row: EventFrameRow) => onSelect(row.primary) : undefined}
        rowClassName={(row: EventFrameRow) => TONE_ROW_CLASS[frameTone(row)]}
        tableClassName="text-xs"
        emptyMessage={t("operator.telemetry.events_empty")}
      />
    </DataTableShell>
  );
}
